// meetings-records.ts — Endpoints réunions & notes collaboratives.
import { req, uploadBlobWithFilename } from './_internals.js';

export const meetingsRecordsApi = {
  getMeetingsRecords: (params?: {
    project_id?: number;
    committee_id?: number;
    date_from?: string;
    date_to?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.project_id) qs.set('project_id', String(params.project_id));
    if (params?.committee_id) qs.set('committee_id', String(params.committee_id));
    if (params?.date_from) qs.set('date_from', params.date_from);
    if (params?.date_to) qs.set('date_to', params.date_to);
    const q = qs.toString();
    return req('GET', '/meetings-records' + (q ? '?' + q : ''));
  },
  // Réunion unique (page détail #meeting/:id) — évite de télécharger toute
  // la liste pour afficher une seule réunion.
  getMeetingRecord: (id: number) => req('GET', '/meetings-records/' + id),
  // Indicateurs d'actions mises à jour autour de la réunion (onglet Indicateurs).
  getMeetingActionStats: (id: number) => req('GET', '/meetings-records/' + id + '/action-stats'),
  createMeetingRecord: (d: unknown) => req('POST', '/meetings-records', d),
  updateMeetingRecord: (id: number, d: unknown) => req('PUT', '/meetings-records/' + id, d),
  deleteMeetingRecord: (id: number) => req('DELETE', '/meetings-records/' + id),
  openMeetingRecord: (id: number) => req('POST', '/meetings-records/' + id + '/open', {}),
  closeMeetingRecord: (id: number) => req('POST', '/meetings-records/' + id + '/close', {}),
  // `detail=true` renvoie aussi les actions complètes (perf #9 : évite de
  // charger toutes les actions du tenant pour l'onglet Actions de la réunion).
  getMeetingActions: (meetingId: number, detail = false) =>
    req('GET', '/meetings-records/' + meetingId + '/actions' + (detail ? '?detail=true' : '')),
  setMeetingActions: (meetingId: number, actionIds: number[]) =>
    req('PUT', '/meetings-records/' + meetingId + '/actions', { action_ids: actionIds }),
  listMeetingDraftActions: (meetingId: number) =>
    req('GET', '/meetings-records/' + meetingId + '/draft-actions'),
  createMeetingDraftAction: (
    meetingId: number,
    payload: {
      text?: string | null;
      approver_id?: number | null;
      deadline?: string | null;
      performers?: number[];
      priority?: number;
    },
  ) => req('POST', '/meetings-records/' + meetingId + '/draft-actions', payload),
  patchMeetingDraftAction: (
    meetingId: number,
    actionId: number,
    payload: { text?: string | null; approver_id?: number | null; deadline?: string | null },
  ) => req('PATCH', '/meetings-records/' + meetingId + '/draft-actions/' + actionId, payload),
  deleteMeetingDraftAction: (meetingId: number, actionId: number) =>
    req('DELETE', '/meetings-records/' + meetingId + '/draft-actions/' + actionId),
  publishMeetingDraftAction: (meetingId: number, actionId: number) =>
    req('POST', '/meetings-records/' + meetingId + '/draft-actions/' + actionId + '/publish', {}),
  bulkLinkMeetingDraftActions: (
    meetingId: number,
    payload: { project_id?: number | null; committee_id?: number | null },
  ) => req('POST', '/meetings-records/' + meetingId + '/draft-actions/bulk-link', payload),
  getMyMeetingNotes: (meetingId: number) =>
    req('GET', '/meetings-records/' + meetingId + '/my-notes'),
  saveMyMeetingNotes: (meetingId: number, content: string) =>
    req('PUT', '/meetings-records/' + meetingId + '/my-notes', { content }),
  getAllMeetingNotes: (meetingId: number) =>
    req('GET', '/meetings-records/' + meetingId + '/all-notes'),
  generateMeetingCR: (
    meetingId: number,
    supportFile: File | null,
    includedUserIds?: number[],
  ): Promise<string> => {
    const fd = new FormData();
    if (supportFile) fd.append('support', supportFile);
    if (includedUserIds !== undefined)
      fd.append('included_user_ids', JSON.stringify(includedUserIds));
    return uploadBlobWithFilename(
      '/meetings-records/' + meetingId + '/generate-cr',
      fd,
      `CR_reunion_${meetingId}.docx`,
    );
  },
};
